'use client';

import { useState, useEffect, useCallback } from 'react';
import { consentManagerAdapter } from '@custom/_exports';

export type ConsentCategory =
  | 'necessary'
  | 'preferences'
  | 'statistics'
  | 'marketing';

export interface UseConsentManagerOptions {
  category?: ConsentCategory;
  onConsentChange?: (hasConsent: boolean) => void;
}

export interface UseConsentManagerResult {
  hasConsent: boolean;
  isReady: boolean;
  openConsentDialog: () => void;
  acceptCategory: () => void;
}

// The adapter contract that custom implementations must fulfill
export interface ConsentManagerAdapter {
  /** Check consent status for a given category. Returns boolean or null if not ready. */
  checkConsent: (category: ConsentCategory) => boolean | null;
  /** Open the consent dialog for the user to change preferences */
  openConsentDialog: () => void;
  /** Subscribe to consent changes. Returns an unsubscribe function. */
  subscribe: (callback: () => void) => () => void;
  /** Check if the consent manager script has loaded */
  isAvailable: () => boolean;
  /** Whether the consent manager is actually configured and expected to load (e.g. script tag in DOM) */
  isConfigured: () => boolean;
  /** Update the consent banner language after client-side navigation */
  updateLanguage?: (locale: string) => void;
  /** Accept a specific consent category directly (without opening dialog) */
  acceptCategory?: (category: ConsentCategory) => void;
}

/**
 * Generic consent management hook.
 *
 * Provider-agnostic: the actual consent checking logic comes from
 * the adapter in @custom/_exports (Cookiebot, Usercentrics, etc.)
 *
 * If no custom adapter is configured, falls back to granting all consent.
 */
export function useConsentManager(
  options: UseConsentManagerOptions = {}
): UseConsentManagerResult {
  const { category = 'statistics', onConsentChange } = options;

  const [hasConsent, setHasConsent] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const checkConsent = useCallback(() => {
    const result = consentManagerAdapter.checkConsent(category);
    if (result !== null) {
      setHasConsent(result);
      setIsReady(true);
      onConsentChange?.(result);
      return true;
    }
    return false;
  }, [category, onConsentChange]);

  const openConsentDialog = useCallback(() => {
    consentManagerAdapter.openConsentDialog();
  }, []);

  const acceptCategory = useCallback(() => {
    if (consentManagerAdapter.acceptCategory) {
      consentManagerAdapter.acceptCategory(category);
    } else {
      // Fallback: open dialog if direct accept not supported
      consentManagerAdapter.openConsentDialog();
    }
  }, [category]);

  // Legitimate effect: reads initial consent from the (external) consent
  // manager adapter on mount and subscribes to changes. The setState calls sync
  // React to that external system — a GDPR-critical integration we deliberately
  // don't restructure. Rule disabled with intent.
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    const found = checkConsent();

    // No consent manager configured — grant all consent immediately
    if (!found && !consentManagerAdapter.isConfigured()) {
      setHasConsent(true);
      setIsReady(true);
      return;
    }
    /* eslint-enable react-hooks/set-state-in-effect */

    // Consent manager configured — wait for events (no fixed timeout)
    const unsubscribe = consentManagerAdapter.subscribe(() => {
      checkConsent();
    });

    return () => {
      unsubscribe();
    };
  }, [checkConsent]);

  return { hasConsent, isReady, openConsentDialog, acceptCategory };
}
